import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import MainContainer from "./Components/MainContainer";
import AuthPage from "./Components/AuthPage";
import supabase from "./supabaseClient";
import ResetPassword from "./Components/ResetPassword";
import NameInputModal from "./Components/NameInputModal"
import AuthCallback from "./AuthCallback";
import AdminAnalysisPage from "./Components/AdminAnalysisPage";
import MainLogo from "./Assets/main-logo.svg";
import "./App.css";
import {
  isPasskeyEnabledForUser,
  isPasskeySupported,
  verifyLocalPasskey,
} from "./auth/passkey";
import {
  getOnlineStatus,
  initializePushNotifications,
  initializeSystemUi,
  isNativeApp,
  listenAppState,
  listenNetworkChanges,
} from "./native/capacitor";
import {
  clearCachedAuthSnapshot,
  flushOfflineQueue,
  getCachedAuthSnapshot,
  getCachedProfile,
  getPendingQueueSize,
  setCachedAuthSnapshot,
  setCachedProfile,
} from "./offline/sessionOffline";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AppLaunchScreen() {
  return (
    <div className="app-launch-screen">
      <div className="app-launch-card">
        <img src={MainLogo} alt="PaiCoach" className="app-launch-logo" />
        <p className="app-launch-text">Preparing your session...</p>
        <div className="app-launch-spinner" aria-hidden="true" />
      </div>
    </div>
  );
}

function AdminGate({ session, profile, authReady, profileLoading }) {
  const location = useLocation();

  if (!authReady || profileLoading) {
    console.log("[AdminGate] waiting", { authReady, profileLoading, hasSession: !!session, isAdmin: profile?.is_admin });
    return <AppLaunchScreen />;
  }

  if (session && profile?.is_admin) {
    console.log("[AdminGate] allow admin", { user: session.user?.id });
    return <AdminAnalysisPage session={session} profile={profile} />;
  }

  console.log("[AdminGate] redirecting to login", {
    authReady,
    profileLoading,
    hasSession: !!session,
    isAdmin: profile?.is_admin
  });
  return (
    <Navigate
      replace
      to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
    />
  );
}

function App() {
  const [session, setSession] = useState(undefined);
  const [isGuest, setIsGuest] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNameModal, setShowNameModal] = useState(false);
  const [parentName, setParentName] = useState("");
  const [childName, setChildName] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [appKey, setAppKey] = useState(0);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [initialChecked, setInitialChecked] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [unlockChecked, setUnlockChecked] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (isNativeApp()) {
      root.classList.add("native-app-shell");
      root.classList.remove("web-browser-shell");
    } else {
      root.classList.add("web-browser-shell");
      root.classList.remove("native-app-shell");
    }
    return () => {
      root.classList.remove("native-app-shell");
      root.classList.remove("web-browser-shell");
    };
  }, []);

  useEffect(() => {
    let removeNetworkListener = () => {};
    let removeAppListener = () => {};
    let removePushListener = () => {};

    const initNativeShell = async () => {
      const online = await getOnlineStatus();
      setIsOnline(online);
      removeNetworkListener = await listenNetworkChanges(setIsOnline);

      if (!isNativeApp()) return;

      await initializeSystemUi();

      removeAppListener = await listenAppState(async ({ isActive }) => {
        if (!isActive) return;
        setRefreshTrigger((prev) => prev + 1);
      });

      removePushListener = await initializePushNotifications(async (token) => {
        try {
          if (!REACT_APP_API_BASE_URL) return;
          await fetch(`${REACT_APP_API_BASE_URL}/api/push-token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, userId: session?.user?.id || null }),
          });
        } catch (_) {}
      }, (data) => {
        const sessionId = data?.restore || data?.videoId || data?.sessionId;
        if (!sessionId) return;
        window.location.assign(`/Project-Demo?restore=${encodeURIComponent(sessionId)}`);
      });
    };

    initNativeShell();

    return () => {
      removeNetworkListener();
      removeAppListener();
      removePushListener();
    };
  }, [session?.user?.id]);

  useEffect(() => {
    let cancelled = false;

    const syncPending = async () => {
      setPendingSyncCount(getPendingQueueSize());
      if (!isOnline) return;
      const result = await flushOfflineQueue(REACT_APP_API_BASE_URL);
      if (cancelled) return;
      setPendingSyncCount(result.remaining);
      if (result.processed > 0) {
        setRefreshTrigger((prev) => prev + 1);
      }
    };

    syncPending();
    return () => {
      cancelled = true;
    };
  }, [isOnline, refreshTrigger]);

  useEffect(() => {
    let cancelled = false;

    const enforcePasskeyUnlock = async () => {
      if (!session?.user?.id) {
        setUnlockChecked(true);
        return;
      }
      const userId = session.user.id;
      const needsUnlock = isPasskeyEnabledForUser(userId);
      if (!needsUnlock) {
        setUnlockChecked(true);
        return;
      }
      if (!isPasskeySupported()) {
        setUnlockChecked(true);
        return;
      }

      const ok = await verifyLocalPasskey({ userId });
      if (cancelled) return;

      if (!ok) {
        await supabase.auth.signOut();
      }
      setUnlockChecked(true);
    };

    setUnlockChecked(false);
    enforcePasskeyUnlock();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const ensureProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setProfileLoading(false);
      setProfileReady(true);
      return;
    }
    if (profileReady && profile?.id === userId) {
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    const { data: userProfile, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (!error) {
      setProfile(userProfile);
      setCachedProfile(userProfile);
      if (!userProfile?.parent_name || !userProfile?.child_name) {
        setShowNameModal(true);
      }
    } else if (!isOnline) {
      const cached = getCachedProfile();
      if (cached?.profile?.id === userId) {
        setProfile(cached.profile);
      }
    }
    console.log("[Auth] profile fetch", { userId, ok: !error, isAdmin: userProfile?.is_admin });
    setProfileLoading(false);
    setProfileReady(true);
  }, [isOnline, profileReady, profile]);

  useEffect(() => {
    const getSession = async () => {
      setLoading(true);
      let liveSession = null;
      try {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        liveSession = currentSession || null;
      } catch (_) {
        liveSession = null;
      }

      if (liveSession?.user?.id) {
        setCachedAuthSnapshot(liveSession);
      }

      if (!liveSession && !isOnline) {
        const cachedAuth = getCachedAuthSnapshot();
        if (cachedAuth?.user?.id) {
          liveSession = { user: cachedAuth.user, offlineCached: true };
          const cachedProfile = getCachedProfile();
          if (cachedProfile?.profile?.id === cachedAuth.user.id) {
            setProfile(cachedProfile.profile);
            setProfileReady(true);
            setProfileLoading(false);
          }
        }
      }

      setSession(liveSession || null);
      setLoading(false);
      if (liveSession?.user) {
        ensureProfile(liveSession.user.id);
      } else {
        setProfile(null);
        setProfileLoading(false);
        setProfileReady(true);
      }
      setInitialChecked(true);
      console.log("[Auth] initial session", { hasSession: !!liveSession, user: liveSession?.user?.id });
    };

    getSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session || null);
        if (session?.user?.id) {
          setCachedAuthSnapshot(session);
        } else if (event === "SIGNED_OUT") {
          clearCachedAuthSnapshot();
        }
        if (session?.user) {
          ensureProfile(session.user.id);
        } else {
          setProfile(null);
          setProfileLoading(false);
          setProfileReady(true);
        }
        setInitialChecked(true);
      }
    );

    return () => {
      authListener.subscription?.unsubscribe();
    };
  }, [ensureProfile, isOnline]);

  useEffect(() => {
    if (!profile?.id) return;
    setCachedProfile(profile);
  }, [profile]);

  // keep guest modal behavior unchanged
  useEffect(() => {
    if (isGuest && (!parentName || !childName)) {
      setShowNameModal(true);
    }
  }, [isGuest, parentName, childName]);

  const showSplash = (!initialChecked && loading) || !unlockChecked;

  if (showSplash) {
    return <AppLaunchScreen />;
  }

  return (
    <>
      {!isOnline && (
        <div
          style={{
            width: "100%",
            background: "#B42318",
            color: "#fff",
            padding: "10px 14px",
            textAlign: "center",
            fontWeight: 600,
            fontSize: "0.92rem",
            zIndex: 9999,
            position: "fixed",
            top: "env(safe-area-inset-top)",
            left: 0,
            right: 0,
          }}
        >
          You are offline. Some actions may be delayed until connection returns.
        </div>
      )}
      {isOnline && pendingSyncCount > 0 && (
        <div
          style={{
            width: "100%",
            background: "#1D4ED8",
            color: "#fff",
            padding: "8px 12px",
            textAlign: "center",
            fontWeight: 600,
            fontSize: "0.86rem",
            zIndex: 9998,
            position: "fixed",
            top: "env(safe-area-inset-top)",
            left: 0,
            right: 0,
          }}
        >
          Syncing {pendingSyncCount} offline action{pendingSyncCount > 1 ? "s" : ""}...
        </div>
      )}
      <Router>
        {showNameModal && (
          <NameInputModal
            userId={session?.user?.id}
            setProfile={setProfile}
            setShowNameModal={setShowNameModal}
            setParentName={setParentName}
            setChildName={setChildName}
            isGuest={isGuest}
            profile={profile}
            refreshTrigger={refreshTrigger}
            setRefreshTrigger={setRefreshTrigger}
          />
        )}
        <Routes>
          <Route
            path="/"
            element={
              session || isGuest ? (
                <Navigate to="/Project-Demo" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/login" element={<AuthPage setIsGuest={setIsGuest} profile={profile} />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/Project-Demo"
            element={
              session || isGuest ? (
                <MainContainer key={appKey} onAppReload={() => setAppKey(prev => prev + 1)} userId={session?.user?.id} profile={profile} setShowNameModal={setShowNameModal} isGuest={isGuest} isMobile={isMobile} refreshTrigger={refreshTrigger} setProfile={setProfile} isOnline={isOnline} />
              ) : (
                <Navigate to={`/login?redirect=/Project-Demo${window.location.search}`}/>
              )
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/admin"
            element={
              <AdminGate
                session={session}
                profile={profile}
                authReady={initialChecked}
                profileLoading={profileLoading}
              />
            }
          />
        </Routes>
      </Router>
    </>
  );
}

export default App;
